/**
 * Everything attached to the tax profile, one row per document.
 *
 * Expects the documents the api sent, the callbacks that read a document and save a tax year, the
 * callback that opens a document's summary screen (ReviewModal), the callback that opens a document's file, the
 * callback that opens the Add document modal, and the ids of the row whose write is in flight and
 * of the row to mark with HIGHLIGHT_ROW_CLASS.
 *
 * Renders a heading with an Add document button beside it and, under them, a table whose columns
 * are what the document is and the name of the copy in the client's Files folder, the tax year it
 * is for, when it arrived and from whom, where its reading stands, and the row's own buttons. The
 * title of a document that has been read is a button that opens its summary screen: every value it gave and what became of each. The
 * file name under the title is a link-colored button that opens the file (onOpenDocument) when the
 * document has a copy in OneDrive (onedrive_item_id), and plain text otherwise.
 *
 * A document the check before promotion held (entityHold) says why
 * in its Status cell and offers, in its Actions cell, the buttons entityHoldButtons in wording.ts
 * gives: answers the page sends through onAnswerEntity, and "Open that client", which calls
 * onOpenClient.
 *
 * The table sends no request of its own, so a test can render it without a network.
 */

import { useEffect, useState } from 'react';
import type { OcrDocumentType } from '@shared/constants/taxProfile';
import type {
  AnswerTaxDocumentEntityRequest,
  TaxDocumentListItem,
} from '@shared/types/taxProfile';
import ChooseDocumentTypeModal from '@/pages/TaxProfile/components/ChooseDocumentTypeModal';
import DocumentStatus from '@/pages/TaxProfile/components/DocumentStatus';
import { HIGHLIGHT_ROW_CLASS } from '@/pages/TaxProfile/components/FieldRow';
import { LIST_TABLE_LAYOUT } from '@/pages/TaxProfile/components/ListTable';
import {
  addedSentence,
  addedTooltip,
  documentStatusWording,
  documentTitle,
  entityHoldButtons,
  openDocumentLabel,
} from '@/pages/TaxProfile/wording';
import { BRAND_HEADING_CLASS } from '@/pages/TaxProfile/brand';

/**
 * The classes of the table, its parts and its cells for the two layouts: the owners table's
 * (LIST_TABLE_LAYOUT in ListTable.tsx) for the table, its header and its body, with daisyUI's
 * table-zebra added to the table; below lg the header is hidden and each row wraps, from lg up the
 * table is an ordinary fixed table. The row and the cells have their own classes, because the
 * owners table's carry a left edge and a line under each cell that this table does not draw.
 */
const DOCUMENTS_TABLE_LAYOUT = {
  table: `${LIST_TABLE_LAYOUT.table} table-zebra`,
  head: LIST_TABLE_LAYOUT.head,
  body: LIST_TABLE_LAYOUT.body,
  row: 'flex flex-wrap lg:table-row',
  /** The title and file name: a line of their own below lg. */
  documentCell: 'whitespace-normal w-full lg:w-auto',
  /** Year, Added and Status: two to a line, three from sm, with the header above each below lg. */
  cell:
    'whitespace-normal w-1/2 sm:w-1/3 grow lg:w-auto ' +
    'before:block before:content-[attr(data-label)] before:text-xs before:font-bold ' +
    'before:uppercase before:opacity-60 before:mb-1 lg:before:hidden',
  /** The buttons: the last line below lg, and hidden there while the row has none. */
  actionsCell:
    'whitespace-normal w-full lg:w-auto text-right empty:hidden lg:empty:table-cell',
} as const;

/** The id of this section, which the section index and the add toast's link scroll to. */
export const DOCUMENTS_SECTION_ID = 'tax-profile-documents';

/** The id of one document's row, which the left column's finished lines scroll to. */
export const documentRowId = (documentId: number) => `document-${documentId}`;

/**
 * Brings the element with that id into view: into the middle of the window by default, or with its
 * top at the top of the window for block 'start', which SectionIndex asks for because a section
 * taller than the window would otherwise have its heading scrolled out of view.
 */
export const scrollToId = (
  elementId: string,
  block: ScrollLogicalPosition = 'center'
) => {
  window.document
    .getElementById(elementId)
    ?.scrollIntoView({ behavior: 'smooth', block });
};

/**
 * The label of the row's Review button: 'Review' while the reading left values waiting for a
 * person, and null otherwise, which draws no button. A document that has been read with nothing
 * waiting offers "Extraction summary" instead (offersSummary below).
 */
export const reviewButtonLabel = (
  document: TaxDocumentListItem
): string | null => (document.waitingCount > 0 ? 'Review' : null);

/**
 * True for a read document with nothing left waiting, whose row offers "Extraction summary". It
 * opens the same screen as the title and as Review, so the row has a button to press rather than
 * only a title that has to be guessed at as a link.
 *
 * A held document (entityHold) is left out: nothing from its reading was recorded, and its row's
 * buttons are the answers to where it belongs.
 */
export const offersSummary = (document: TaxDocumentListItem): boolean =>
  document.extraction_status === 'extracted' &&
  !reviewButtonLabel(document) &&
  document.entityHold === null;

export interface DocumentsTableProps {
  documents: TaxDocumentListItem[];
  /**
   * Asks the api to read the document, from both "Parse document" and "Try again". The type is
   * passed only for a row whose last reading could not work one out: that row's button says "Add
   * document type", opens ChooseDocumentTypeModal first, and calls this with what was picked there.
   */
  onReadDocument: (
    documentId: number,
    docType?: OcrDocumentType
  ) => void | Promise<void>;
  /**
   * Opens the document's summary screen, from its title, from "Review" and from "Extraction
   * summary". The screen only reads; "Review" says values of the document still wait on the page.
   */
  onReviewDocument?: (documentId: number) => void;
  /** Opens the document's file, from the file name under its title. */
  onOpenDocument?: (documentId: number) => void;
  /** Saves the tax year a person typed in the Year cell. */
  onUpdateYear: (
    documentId: number,
    docYear: number | null
  ) => void | Promise<void>;
  /**
   * Opens the Add document modal, from the button beside the Documents heading. Left out, that
   * button is not drawn: the page leaves it out while the client has no legal entity yet.
   */
  onAddDocument?: () => void;
  /**
   * Sends a person's answer about a held document: "Add to this company anyway" and "Keep here"
   * send keep_here, "Move there" sends move with the entity the document's name matches. Left out,
   * a held row draws none of those buttons.
   */
  onAnswerEntity?: (
    documentId: number,
    answer: AnswerTaxDocumentEntityRequest
  ) => void | Promise<void>;
  /**
   * Switches the page to the other client a held document's EIN belongs to, from "Open that
   * client". Left out, the button is not drawn.
   */
  onOpenClient?: (client: { id: number; client_name: string }) => void;
  /**
   * Whether the person may view that other client; "Open that client" is drawn only when this
   * answers true. Left out, every client counts as one they may view.
   */
  canOpenClient?: (clientId: number) => boolean;
  /**
   * True while a read, a year change or an answer is in flight, which disables the row's controls.
   */
  busyDocumentId?: number | null;
  /** The row the left column has just sent the page to; it carries HIGHLIGHT_ROW_CLASS. */
  highlightedDocumentId?: number | null;
  /** Added to the section's own classes; TaxProfile.tsx passes the glow of the section in view. */
  className?: string;
}

/**
 * The name of the copy in the client's Files folder, which the row shows under the title. The name
 * the file arrived under is on the document's summary screen, under its heading.
 */
const storedFileName = (document: TaxDocumentListItem): string =>
  document.onedrive_filename ?? document.original_filename ?? '';

function DocumentYearCell({
  document,
  onUpdateYear,
  disabled,
}: {
  document: TaxDocumentListItem;
  onUpdateYear: DocumentsTableProps['onUpdateYear'];
  disabled: boolean;
}) {
  const storedText =
    document.doc_year === null ? '' : String(document.doc_year);
  const [typed, setTyped] = useState(storedText);

  useEffect(() => {
    setTyped(storedText);
  }, [storedText]);

  /**
   * Saves what was typed, on Enter and on leaving the box.
   *
   * On leaving as well as on Enter, because typing a year and clicking away is what a person does,
   * and without it the box kept the year on screen without sending it. It sends nothing when the
   * text is what the row already carries, so leaving the box untouched costs no request.
   */
  const save = () => {
    const trimmed = typed.trim();
    if (trimmed === storedText) return undefined;
    return onUpdateYear(document.id, trimmed === '' ? null : Number(trimmed));
  };

  return (
    <input
      type='number'
      // w-16 so the box and the cell's padding together fit inside the Year column's w-20.
      className='input input-xs input-ghost w-16'
      aria-label={`Tax year for ${documentTitle(document)}`}
      placeholder='—'
      value={typed}
      disabled={disabled}
      onChange={(event) => setTyped(event.target.value)}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onBlur={save}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return undefined;
        event.preventDefault();
        return save();
      }}
    />
  );
}

export default function DocumentsTable({
  documents,
  onReadDocument,
  onReviewDocument,
  onOpenDocument,
  onUpdateYear,
  onAddDocument,
  onAnswerEntity,
  onOpenClient,
  canOpenClient = () => true,
  busyDocumentId = null,
  highlightedDocumentId = null,
  className = '',
}: DocumentsTableProps) {
  /**
   * The row whose "Add document type" button was pressed, which is the row ChooseDocumentTypeModal
   * asks about. It is null while no row is being asked about, and the modal is then not rendered at
   * all, so the type picked last time is gone when it opens again.
   */
  const [choosingTypeFor, setChoosingTypeFor] =
    useState<TaxDocumentListItem | null>(null);

  return (
    <section
      id={DOCUMENTS_SECTION_ID}
      className={`mb-6 scroll-mt-4 ${className}`}
    >
      <div className='flex items-center justify-between mb-1'>
        <h3 className={`font-bold ${BRAND_HEADING_CLASS}`}>Documents</h3>
        {onAddDocument && (
          <button
            type='button'
            className='btn btn-accent btn-xs'
            onClick={onAddDocument}
          >
            Add document
          </button>
        )}
      </div>
      {documents.length === 0 ? (
        <p className='text-xs opacity-70'>
          No documents are attached to this tax profile yet.
        </p>
      ) : (
        // table-fixed and the widths on the header cells decide the columns, not the cells'
        // content. daisyUI puts white-space: nowrap on every cell of .table, so under the browser's
        // own table layout the file name under a title, which is never broken, made the Document
        // column as wide as the longest file name and pushed the columns after it off the screen.
        //
        // The table has no minimum width and no wrapper that scrolls sideways, so it is exactly as
        // wide as the page's main column, like the tables ProfileGroup.tsx draws above it.
        //
        // Below lg it uses the owners table's layout (DOCUMENTS_TABLE_LAYOUT): the header is
        // hidden and each row wraps, the title and file name on a line of their own, then Year,
        // Added and Status with their header above each, then the row's buttons. The column widths
        // below apply from lg up.
        //
        // Year is the one column of a fixed size, because the box in it is a number input of 4rem
        // that cannot be narrower and still show four digits. The other four are percentages, which
        // the browser scales down together when the four of them plus those 5rem do not fit, so a
        // narrow window takes width off all four instead of off the Document column alone. Actions
        // is the smallest share, because most rows carry one short button (Review), while the
        // Added sentence wrapped onto four lines when Actions took 30%. The longest label a row's
        // button carries, 'Add document type', measures 160 pixels on one line: it sits on one
        // line on a table of about 1,000 pixels or more, and wraps onto two lines inside the
        // button on a narrower one, which is what whitespace-normal on that cell is for.
        <table className={DOCUMENTS_TABLE_LAYOUT.table}>
          <thead className={DOCUMENTS_TABLE_LAYOUT.head}>
            <tr>
              <th className='w-[30%]'>Document</th>
              <th className='w-20'>Year</th>
              <th className='w-[26%]'>Added</th>
              <th className='w-[26%]'>Status</th>
              <th className='w-[18%]' aria-label='Actions' />
            </tr>
          </thead>
          <tbody className={DOCUMENTS_TABLE_LAYOUT.body}>
            {documents.map((document) => {
              const wording = documentStatusWording(document);
              const busy = busyDocumentId === document.id;
              const holdButtons = document.entityHold
                ? entityHoldButtons(document.entityHold).filter((button) =>
                    button.kind === 'open-client'
                      ? onOpenClient !== undefined &&
                        canOpenClient(button.client.id)
                      : onAnswerEntity !== undefined
                  )
                : [];
              const highlight =
                highlightedDocumentId === document.id
                  ? HIGHLIGHT_ROW_CLASS
                  : '';
              return (
                <tr
                  key={document.id}
                  id={documentRowId(document.id)}
                  className={`${DOCUMENTS_TABLE_LAYOUT.row} scroll-mt-4 ${highlight}`}
                  data-testid={`tax-document-${document.id}`}
                  data-highlighted={highlight ? true : undefined}
                >
                  {/* whitespace-normal so the document's title wraps onto a second line on a
                      narrow table instead of running into the Year column. */}
                  <td className={DOCUMENTS_TABLE_LAYOUT.documentCell}>
                    {/* min-w-0 lets the file name below shrink under the column's width, which is
                          what truncate needs to cut it and add the ellipsis. */}
                    <div className='flex flex-col min-w-0'>
                      {onReviewDocument &&
                      document.extraction_status === 'extracted' ? (
                        <button
                          type='button'
                          className='link link-hover text-left'
                          onClick={() => onReviewDocument(document.id)}
                        >
                          {documentTitle(document)}
                        </button>
                      ) : (
                        <span>{documentTitle(document)}</span>
                      )}
                      {onOpenDocument && document.onedrive_item_id ? (
                        <button
                          type='button'
                          className='link link-primary link-hover text-xs text-left truncate max-w-full'
                          title={storedFileName(document)}
                          aria-label={openDocumentLabel(
                            storedFileName(document)
                          )}
                          onClick={() => onOpenDocument(document.id)}
                        >
                          {storedFileName(document)}
                        </button>
                      ) : (
                        <span
                          className='text-xs opacity-70 truncate'
                          title={storedFileName(document)}
                        >
                          {storedFileName(document)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={DOCUMENTS_TABLE_LAYOUT.cell} data-label='Year'>
                    <DocumentYearCell
                      document={document}
                      onUpdateYear={onUpdateYear}
                      disabled={busy}
                    />
                  </td>
                  {/* Both cells wrap, so a narrow window stacks the date and the person, and the
                        sentence under a badge, instead of running past the column. */}
                  <td
                    className={DOCUMENTS_TABLE_LAYOUT.cell}
                    data-label='Added'
                    title={addedTooltip(document)}
                  >
                    {addedSentence(document)}
                  </td>
                  <td
                    className={DOCUMENTS_TABLE_LAYOUT.cell}
                    data-label='Status'
                  >
                    <DocumentStatus document={document} />
                  </td>
                  {/* whitespace-normal, against daisyUI's nowrap on every cell of .table, so the
                      button's label wraps inside the button on a narrow table instead of the
                      button running past the cell. */}
                  <td className={DOCUMENTS_TABLE_LAYOUT.actionsCell}>
                    {wording.action && (
                      <button
                        type='button'
                        className='btn btn-accent btn-xs'
                        disabled={busy}
                        // 'choose-type' is the one action that asks the person something before
                        // anything is sent. documentStatusWording gives it to a row whose reading
                        // failed with 'unknown_document_kind', where sending the id alone would
                        // fail that document the same way again. Every other row sends its id and
                        // nothing else.
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onClick={() =>
                          wording.action === 'choose-type'
                            ? setChoosingTypeFor(document)
                            : onReadDocument(document.id)
                        }
                      >
                        {wording.actionLabel}
                      </button>
                    )}
                    {onReviewDocument && reviewButtonLabel(document) && (
                      <button
                        type='button'
                        className='btn btn-accent btn-xs ml-2'
                        disabled={busy}
                        onClick={() => onReviewDocument(document.id)}
                      >
                        {reviewButtonLabel(document)}
                      </button>
                    )}
                    {/* Outlined rather than filled: nothing on this row waits for a person, so it
                        draws less attention than Review does. */}
                    {onReviewDocument && offersSummary(document) && (
                      <button
                        type='button'
                        className='btn btn-outline btn-accent btn-xs ml-2'
                        disabled={busy}
                        onClick={() => onReviewDocument(document.id)}
                      >
                        Extraction summary
                      </button>
                    )}
                    {/* A held document's buttons wrap onto more lines rather than running past
                        the cell, and each label wraps inside its button (h-auto lets the button
                        grow), in both layouts. */}
                    {holdButtons.length > 0 && (
                      <div className='flex flex-wrap justify-end gap-1'>
                        {holdButtons.map((button) => (
                          <button
                            key={button.label}
                            type='button'
                            className={`btn btn-xs h-auto min-h-6 py-1 ${
                              button.kind === 'open-client'
                                ? 'btn-outline btn-accent'
                                : 'btn-accent'
                            }`}
                            disabled={busy}
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onClick={() =>
                              button.kind === 'open-client'
                                ? onOpenClient?.(button.client)
                                : onAnswerEntity?.(document.id, button.answer)
                            }
                          >
                            {button.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {choosingTypeFor && (
        <ChooseDocumentTypeModal
          fileName={storedFileName(choosingTypeFor)}
          onClose={() => setChoosingTypeFor(null)}
          onConfirm={(docType) => {
            const documentId = choosingTypeFor.id;
            setChoosingTypeFor(null);
            return onReadDocument(documentId, docType);
          }}
        />
      )}
    </section>
  );
}
